<template>
    <motion.div
        :initial="false"
        :animate="target"
        :transition="{ duration: reducedMotion ? 0 : props.duration / 1000, ease: [0.25, 0.1, 0.25, 1] }"
        :class="{ 'overflow-hidden': animating || 'auto' !== props.height }"
        :aria-hidden="0 === props.height ? true : undefined"
        @animationComplete="onAnimationComplete"
    >
        <div v-show="!collapsed" :class="props.contentClass">
            <slot></slot>
        </div>
    </motion.div>
</template>

<script setup lang="ts">
import { motion, useReducedMotion, type MotionProps } from 'motion-v';
import { computed, ref, watch } from 'vue';

const props = withDefaults(
    defineProps<{
        height: 'auto' | number;
        duration?: number;
        animateOpacity?: boolean;
        contentClass?: string;
    }>(),
    {
        duration: 250,
        animateOpacity: false,
        contentClass: undefined,
    }
);

const emit = defineEmits<{
    animationEnd: [payload: { newHeight: 'auto' | number }];
}>();

const reducedMotion = useReducedMotion();

const animating = ref(false);

const target = computed(() =>
    props.animateOpacity ? { height: props.height, opacity: 0 === props.height ? 0 : 1 } : { height: props.height }
);

// The content is displayed before an expansion starts, so that motion can measure an "auto" height, until a collapse has finished.
const collapsed = computed(() => !animating.value && 0 === props.height);

watch(
    () => props.height,
    () => {
        animating.value = true;
    }
);

const onAnimationComplete = (definition: MotionProps['animate']) => {
    // Stopping an animation past its duration completes it, so a superseded target can report after its replacement starts.
    if (definition !== target.value) {
        return;
    }

    animating.value = false;
    emit('animationEnd', { newHeight: props.height });
};
</script>
